import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Eye, EyeOff } from 'lucide-react';
import TermsDialog from './TermsDialog';
import AntiScamWarning from './AntiScamWarning';

const RegisterClientDialog = ({ isOpen, onOpenChange }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [clientData, setClientData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (clientData.password !== clientData.confirmPassword) {
      alert('Le password non coincidono');
      return;
    }

    // Password validation: 8 chars, uppercase, lowercase, number, special char
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{8,}$/;
    if (!passwordRegex.test(clientData.password)) {
      alert('La password deve contenere almeno 8 caratteri, una maiuscola, una minuscola, un numero e un carattere speciale (!@#$%^&*...)');
      return;
    }

    if (!clientData.first_name || !clientData.last_name) {
      alert('Inserisci nome e cognome');
      return;
    }

    if (!clientData.phone) {
      alert('Inserisci il numero di telefono');
      return;
    }

    if (!termsAccepted) {
      alert('Devi accettare i Termini e Condizioni per completare la registrazione.');
      return;
    }

    if (!privacyAccepted) {
      alert('Devi accettare la Privacy Policy per completare la registrazione.');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/auth/register-client`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: clientData.email,
          password: clientData.password,
          first_name: clientData.first_name,
          last_name: clientData.last_name,
          phone: clientData.phone,
          user_type: 'client',
          terms_accepted: true,
          privacy_accepted: true
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la registrazione');
      }

      alert('Registrazione completata con successo! Ora puoi accedere.');
      onOpenChange(false);
      
      // Reset form
      setClientData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: ''
      });
      setTermsAccepted(false);
      setPrivacyAccepted(false);
      
    } catch (error) {
      console.error('Errore:', error);
      alert('Errore durante la registrazione: ' + error.message);
    }
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">Iscriviti</h2>
          <p className="text-slate-600 mt-1 text-sm">Crea un account per noleggiare piattaforme aeree</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Nome e Cognome */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="client_first_name">Nome *</Label>
              <Input
                id="client_first_name"
                value={clientData.first_name}
                onChange={(e) => setClientData({...clientData, first_name: e.target.value})}
                placeholder="Mario"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="client_last_name">Cognome *</Label>
              <Input
                id="client_last_name"
                value={clientData.last_name}
                onChange={(e) => setClientData({...clientData, last_name: e.target.value})}
                placeholder="Rossi"
                required
              />
            </div>
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="client_email">Email *</Label>
            <Input
              id="client_email"
              type="email"
              value={clientData.email}
              onChange={(e) => setClientData({...clientData, email: e.target.value})}
              placeholder="mario.rossi@example.com"
              required
            />
          </div>

          {/* Telefono */}
          <div className="space-y-2">
            <Label htmlFor="client_phone">Telefono *</Label>
            <Input
              id="client_phone"
              type="tel"
              value={clientData.phone}
              onChange={(e) => setClientData({...clientData, phone: e.target.value})}
              placeholder="+39 333 1234567"
              data-testid="client-phone-input"
              required
            />
          </div>

          {/* Password con icona occhio */}
          <div className="space-y-2">
            <Label htmlFor="client_password">Password *</Label>
            <div className="relative">
              <Input
                id="client_password"
                type={showPassword ? "text" : "password"}
                value={clientData.password}
                onChange={(e) => setClientData({...clientData, password: e.target.value})}
                placeholder="Minimo 8 caratteri"
                className="pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-slate-500">Maiuscola, minuscola, numero e carattere speciale</p>
          </div>

          {/* Conferma Password con icona occhio */}
          <div className="space-y-2">
            <Label htmlFor="client_confirm_password">Conferma Password *</Label>
            <div className="relative">
              <Input
                id="client_confirm_password"
                type={showConfirmPassword ? "text" : "password"}
                value={clientData.confirmPassword}
                onChange={(e) => setClientData({...clientData, confirmPassword: e.target.value})}
                placeholder="••••••••"
                className="pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Termini e Condizioni — obbligatorio */}
          <div className="pt-2">
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                data-testid="client-terms-checkbox"
                required
              />
              <span className="text-sm text-slate-700 leading-snug">
                Dichiaro di aver letto e accettato i{' '}
                <button
                  type="button"
                  onClick={() => setShowTerms(true)}
                  className="text-orange-600 underline hover:text-orange-700 font-medium"
                  data-testid="client-terms-link"
                >
                  Termini e Condizioni
                </button>
                .
              </span>
            </label>
          </div>

          {/* Privacy Policy — obbligatorio */}
          <div className="pt-1">
            <label className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={privacyAccepted}
                onChange={(e) => setPrivacyAccepted(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                data-testid="client-privacy-checkbox"
                required
              />
              <span className="text-sm text-slate-700 leading-snug">
                Dichiaro di aver letto e accettato la{' '}
                <button
                  type="button"
                  onClick={() => setShowPrivacy(true)}
                  className="text-orange-600 underline hover:text-orange-700 font-medium"
                  data-testid="client-privacy-link"
                >
                  Privacy Policy
                </button>
                .
              </span>
            </label>
          </div>

          {/* Anti-scam warning */}
          <AntiScamWarning className="mt-2" testId="register-client-anti-scam-warning" />

          {/* Buttons */}
          <div className="flex space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Annulla
            </Button>
            <Button 
              type="submit"
              className="flex-1 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700"
            >
              Iscriviti
            </Button>
          </div>
        </form>
      </div>
    </div>
      )}
      <TermsDialog isOpen={showTerms} onOpenChange={setShowTerms} termsType="user" />
      <TermsDialog isOpen={showPrivacy} onOpenChange={setShowPrivacy} termsType="privacy" />
    </>
  );
};

export default RegisterClientDialog;
